import React, { useMemo } from 'react';
import { useQuery } from 'react-query';

import { getFeatureFlags } from 'api/feature_flags';
import { FlagKey } from 'api/types';

/*
    Below: provide default values to use as a fallback to response from the API–these 
    values also control the mock responses to stories that use the withFeatureFlags decorator
*/
export const DEFAULT_FLAG_VALUES: Record<FlagKey, boolean> = {
  [FlagKey.achModal]: false,
  [FlagKey.homeownerTransferSelfService]: false,
  [FlagKey.onboardingRefresh]: false,
  [FlagKey.smsOptIn]: false,
  [FlagKey.supportPageV2]: false,
  [FlagKey.systemCategories]: false,
  [FlagKey.systemCategoriesBattery]: false,
  [FlagKey.systemCategoriesGrid]: false,
  [FlagKey.systemCategoriesHome]: false,
  [FlagKey.systemCategoriesSolar]: false,
  [FlagKey.tpv]: false,
  [FlagKey.videoResources]: false,
  [FlagKey.vpp_2024]: false,
  [FlagKey.vpp2025_prelaunch_web]: false,
  [FlagKey.webDarkTheme]: false,
  [FlagKey.webV2_1]: false,
  [FlagKey.webProductionConsumption]: false,
};

type TFeatureFlagContext = Record<FlagKey, boolean>;

interface FeatureFlagContextProps {
  flags: TFeatureFlagContext;
  loading: boolean;
}

const FeatureFlagContext = React.createContext<FeatureFlagContextProps>({
  flags: DEFAULT_FLAG_VALUES,
  loading: true,
});

function FeatureFlagProvider({ children }: { children: React.ReactNode }) {
  const { data: queryData, isLoading } = useQuery(
    'featureFlags',
    () => getFeatureFlags().then((response) => response.data),
    {
      select: (data) =>
        data.reduce((acc: TFeatureFlagContext, flag) => {
          acc[flag.key] = flag.enabled;

          return acc;
        }, DEFAULT_FLAG_VALUES),
      staleTime: Infinity,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );

  const value = useMemo(
    () => ({
      flags: queryData || DEFAULT_FLAG_VALUES,
      loading: isLoading,
    }),
    [queryData, isLoading],
  );

  return <FeatureFlagContext.Provider value={value}>{children}</FeatureFlagContext.Provider>;
}

export { FeatureFlagProvider, FeatureFlagContext };

/*
    Example of usage:

const NewFeature = () => {
    return <Text>New Feature</Text>;
}

const FeatureFlagConsumerExample = () => {
    const {
      flags: { [FlagKey.NewFeature]: newFeature }
    } = useFeatureFlags();

    return (
        <>
        {newFeature && <NewFeature />}
        </>
    );
}
*/
